import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../components/ui/InputField';
import TextAreaField from '../components/ui/TextAreaField';
import Button from '../components/ui/Button';

const KurbanReportForm = ({ onDataChange }) => {
  const { t } = useTranslation();
  
  // 1️⃣ Önce tüm state'leri tanımla
  const [organizationInfo, setOrganizationInfo] = useState({
    organizationName: '',
    orgSubtitle: '',
    projectYear: new Date().getFullYear(),
    location: ''
  });

  const [recipientInfo, setRecipientInfo] = useState({
    clientName: '',
    taxOffice: '',
    taxNumber: '',
    clientAddress: ''
  });

  const [authorizedInfo, setAuthorizedInfo] = useState({
    authorizedName: '',
    authorizedTitle: '',
    authorizedOrganization: ''
  });

  const [contactInfo, setContactInfo] = useState({
    phone: '',
    email: '',
    showBankInfo: false,
    bankName: '',
    accountName: '',
    accountNumber: ''
  });

  // ÖNEMLİ: donations ve expenses useEffect'ten ÖNCE tanımlanmalı
  const [donations, setDonations] = useState([
    { donorName: '', animalType: 'Camel', shareCount: 1, unitPrice: 0, total: 0 }
  ]);

  const [expenses, setExpenses] = useState([
    { expenseItem: '', amount: 0 }
  ]);

  const [projectReport, setProjectReport] = useState('');
  const [notes, setNotes] = useState('');

  // 2️⃣ Şimdi useEffect güvenli
  useEffect(() => {
    const totalIncome = donations.reduce((sum, item) => sum + item.total, 0);
    const totalExpense = expenses.reduce((sum, item) => sum + item.amount, 0);
    const balance = totalIncome - totalExpense;

    onDataChange({
      ...organizationInfo,
      ...recipientInfo,
      ...authorizedInfo,
      ...contactInfo,
      donations,
      expenses,
      projectReport,
      notes,
      totalIncome,
      totalExpense,
      balance,
      date: new Date().toLocaleDateString()
    });
  }, [organizationInfo, recipientInfo, authorizedInfo, contactInfo, donations, expenses, projectReport, notes, onDataChange]);

  // 3️⃣ Handler fonksiyonları
  const handleDonationChange = (index, field, value) => {
    const newDonations = [...donations];
    newDonations[index][field] = value;
    
    if (field === 'shareCount' || field === 'unitPrice') {
      newDonations[index].total = newDonations[index].shareCount * newDonations[index].unitPrice;
    }
    
    setDonations(newDonations);
  };

  const handleExpenseChange = (index, field, value) => {
    const newExpenses = [...expenses];
    newExpenses[index][field] = value;
    setExpenses(newExpenses);
  };

  const addDonation = () => {
    setDonations([...donations, { donorName: '', animalType: 'Camel', shareCount: 1, unitPrice: 0, total: 0 }]);
  };

  const addExpense = () => {
    setExpenses([...expenses, { expenseItem: '', amount: 0 }]);
  };

  const removeDonation = (index) => {
    if (donations.length > 1) {
      setDonations(donations.filter((_, i) => i !== index));
    }
  };

  const removeExpense = (index) => {
    if (expenses.length > 1) {
      setExpenses(expenses.filter((_, i) => i !== index));
    }
  };

  // 4️⃣ Hesaplamalar
  const totalIncome = donations.reduce((sum, item) => sum + item.total, 0);
  const totalExpense = expenses.reduce((sum, item) => sum + item.amount, 0);
  const balance = totalIncome - totalExpense;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 border border-gray-200 dark:border-gray-700 shadow-sm space-y-8">
      {/* Kuruluş Bilgileri */}
      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Organization Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField 
            label="Organization Name (Main Title)" 
            name="organizationName" 
            value={organizationInfo.organizationName} 
            onChange={(e) => setOrganizationInfo({ ...organizationInfo, organizationName: e.target.value })} 
            placeholder="e.g., Organization Name" 
            required 
          />
          <InputField 
            label="Organization Subtitle (English)" 
            name="orgSubtitle" 
            value={organizationInfo.orgSubtitle} 
            onChange={(e) => setOrganizationInfo({ ...organizationInfo, orgSubtitle: e.target.value })} 
            placeholder="e.g., Organization Name in English" 
          />
          <InputField 
            label="Project Year" 
            name="projectYear" 
            type="number" 
            value={organizationInfo.projectYear} 
            onChange={(e) => setOrganizationInfo({ ...organizationInfo, projectYear: e.target.value })} 
          />
          <InputField 
            label="Location" 
            name="location" 
            value={organizationInfo.location} 
            onChange={(e) => setOrganizationInfo({ ...organizationInfo, location: e.target.value })} 
            placeholder="e.g., Mogadishu, Somalia" 
          />
        </div>
      </div>

      {/* Alıcı Bilgileri */}
      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Recipient Details (Bill To)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField 
            label="Client/Organization Name" 
            name="clientName" 
            value={recipientInfo.clientName} 
            onChange={(e) => setRecipientInfo({ ...recipientInfo, clientName: e.target.value })} 
            placeholder="e.g., Organization Name that paids the bill" 
          />
          <InputField 
            label="Tax Office" 
            name="taxOffice" 
            value={recipientInfo.taxOffice} 
            onChange={(e) => setRecipientInfo({ ...recipientInfo, taxOffice: e.target.value })} 
            placeholder="e.g., Sincan Vergi Dairesi" 
          />
          <InputField 
            label="Tax Number" 
            name="taxNumber" 
            value={recipientInfo.taxNumber} 
            onChange={(e) => setRecipientInfo({ ...recipientInfo, taxNumber: e.target.value })} 
            placeholder="e.g., 3820436306" 
          />
          <div className="md:col-span-2">
            <TextAreaField 
              label="Client Address" 
              name="clientAddress" 
              value={recipientInfo.clientAddress} 
              onChange={(e) => setRecipientInfo({ ...recipientInfo, clientAddress: e.target.value })} 
              placeholder="Full address of the recipient organization" 
              rows={2} 
            />
          </div>
        </div>
      </div>

      {/* Bağışlar/Gelir Tablosu */}
      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Donations / Income</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Donor Name</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Animal Type</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Quantity</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Unit Price</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">Total</th>
                <th className="p-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {donations.map((donation, index) => (
                <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2" type="text" value={donation.donorName} onChange={(e) => { const newDonations = [...donations]; newDonations[index].donorName = e.target.value; setDonations(newDonations); }} placeholder="Donor name" /></td>
                  <td className="p-2">
                    <select className="form-select w-full bg-transparent border-0 focus:ring-0 p-2" value={donation.animalType} onChange={(e) => { const newDonations = [...donations]; newDonations[index].animalType = e.target.value; setDonations(newDonations); }}>
                      <option>Camel</option>
                      <option>Cow</option>
                      <option>Goat</option>
                      <option>Sheep</option>
                     {/* <option>Ram</option> */}
                    </select>
                  </td>
                  <td className="p-2"><input className="form-input w-20 bg-transparent border-0 focus:ring-0 p-2 text-center" type="number" min="1" value={donation.shareCount} onChange={(e) => { const newDonations = [...donations]; newDonations[index].shareCount = parseFloat(e.target.value) || 0; newDonations[index].total = newDonations[index].shareCount * newDonations[index].unitPrice; setDonations(newDonations); }} /></td>
                  <td className="p-2"><input className="form-input w-28 bg-transparent border-0 focus:ring-0 p-2 text-right" type="number" min="0" step="0.01" value={donation.unitPrice} onChange={(e) => { const newDonations = [...donations]; newDonations[index].unitPrice = parseFloat(e.target.value) || 0; newDonations[index].total = newDonations[index].shareCount * newDonations[index].unitPrice; setDonations(newDonations); }} /></td>
                  <td className="p-2 text-right font-medium text-[#111418] dark:text-gray-100">${donation.total.toFixed(2)}</td>
                  <td className="p-2 text-center"><button onClick={() => setDonations(donations.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-500" disabled={donations.length === 1}><span className="material-symbols-outlined text-xl">delete</span></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={() => setDonations([...donations, { donorName: '', animalType: 'Camel', shareCount: 1, unitPrice: 0, total: 0 }])} variant="ghost" icon="add_circle" className="mt-4">Add Donation</Button>
        </div>
      </div>

      {/* Giderler Tablosu */}
      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Expenses</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Expense Item</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">Amount</th>
                <th className="p-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense, index) => (
                <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2" type="text" value={expense.expenseItem} onChange={(e) => { const newExpenses = [...expenses]; newExpenses[index].expenseItem = e.target.value; setExpenses(newExpenses); }} placeholder="e.g., Slaughter services, Transportation" /></td>
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2 text-right" type="number" min="0" step="0.01" value={expense.amount} onChange={(e) => { const newExpenses = [...expenses]; newExpenses[index].amount = parseFloat(e.target.value) || 0; setExpenses(newExpenses); }} /></td>
                  <td className="p-2 text-center"><button onClick={() => setExpenses(expenses.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-500" disabled={expenses.length === 1}><span className="material-symbols-outlined text-xl">delete</span></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={() => setExpenses([...expenses, { expenseItem: '', amount: 0 }])} variant="ghost" icon="add_circle" className="mt-4">Add Expense</Button>
        </div>
      </div>

      {/* Finansal Özet */}
      <div className="flex justify-end">
        <div className="w-full max-w-sm space-y-3 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <div className="flex justify-between items-center"><span className="text-gray-600 dark:text-gray-400">Total Income</span><span className="font-medium text-green-600 dark:text-green-400">${donations.reduce((sum, item) => sum + item.total, 0).toFixed(2)}</span></div>
          <div className="flex justify-between items-center"><span className="text-gray-600 dark:text-gray-400">Total Expense</span><span className="font-medium text-red-600 dark:text-red-400">${expenses.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}</span></div>
          <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700"><span className="text-lg font-bold text-[#111418] dark:text-gray-100">Project Balance</span><span className={`text-lg font-bold ${(donations.reduce((sum, item) => sum + item.total, 0) - expenses.reduce((sum, item) => sum + item.amount, 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${(donations.reduce((sum, item) => sum + item.total, 0) - expenses.reduce((sum, item) => sum + item.amount, 0)).toFixed(2)}</span></div>
        </div>
      </div>

      {/* YENİ: Proje Raporu Metni */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Project Report Details</h3>
        <TextAreaField
          label="Project Report / Description"
          value={projectReport}
          onChange={(e) => setProjectReport(e.target.value)}
          placeholder="Describe the project activities, challenges faced, beneficiaries served, and impact achieved. Example: 'In this Kurban 2025 project, we successfully distributed sacrifices to 150 families...'"
          rows={6}
        />
      </div>

      {/* YENİ: Yetkili Kişi Bilgileri */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Authorized Person (Signature)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InputField 
            label="Name" 
            name="authorizedName" 
            value={authorizedInfo.authorizedName} 
            onChange={(e) => setAuthorizedInfo({ ...authorizedInfo, authorizedName: e.target.value })} 
            placeholder="e.g., Name Surname" 
          />
          <InputField 
            label="Title" 
            name="authorizedTitle" 
            value={authorizedInfo.authorizedTitle} 
            onChange={(e) => setAuthorizedInfo({ ...authorizedInfo, authorizedTitle: e.target.value })} 
            placeholder="e.g., President, Director" 
          />
          <InputField 
            label="Organization Name" 
            name="authorizedOrganization" 
            value={authorizedInfo.authorizedOrganization} 
            onChange={(e) => setAuthorizedInfo({ ...authorizedInfo, authorizedOrganization: e.target.value })} 
            placeholder="Organization name" 
          />
        </div>
      </div>

      {/* YENİ: İletişim Bilgileri */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField 
            label="Phone" 
            name="phone" 
            value={contactInfo.phone} 
            onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })} 
            placeholder="Phone Number" 
          />
          <InputField 
            label="Email" 
            name="email" 
            type="email" 
            value={contactInfo.email} 
            onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })} 
            placeholder="info@organization.org" 
          />
        </div>
      </div>

      {/* YENİ: Banka Bilgileri (İsteğe Bağlı) */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <input 
            type="checkbox" 
            id="showBankInfo" 
            checked={contactInfo.showBankInfo} 
            onChange={(e) => setContactInfo({ ...contactInfo, showBankInfo: e.target.checked })} 
            className="w-5 h-5 text-primary" 
          />
          <label 
            htmlFor="showBankInfo" 
            className="text-lg font-bold text-gray-900 dark:text-white cursor-pointer"
          >
            Include Bank Information (Optional)
          </label>
        </div>
        {contactInfo.showBankInfo && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField 
              label="Bank Name" 
              name="bankName" 
              value={contactInfo.bankName} 
              onChange={(e) => setContactInfo({ ...contactInfo, bankName: e.target.value })} 
              placeholder="e.g., Ziraat Katilim Bank" 
            />
            <InputField 
              label="Account Name" 
              name="accountName" 
              value={contactInfo.accountName} 
              onChange={(e) => setContactInfo({ ...contactInfo, accountName: e.target.value })} 
              placeholder="Account holder name" 
            />
            <InputField 
              label="Account Number / IBAN" 
              name="accountNumber" 
              value={contactInfo.accountNumber} 
              onChange={(e) => setContactInfo({ ...contactInfo, accountNumber: e.target.value })} 
              placeholder="Account number" 
            />
          </div>
        )}
      </div>

      {/* Notlar */}
      <TextAreaField 
        label={t('form.notes')} 
        value={notes} 
        onChange={(e) => setNotes(e.target.value)} 
        placeholder="Additional notes about the Kurban project..." 
        rows={3} 
      />
    </div>
  );
};

export default KurbanReportForm;
